import React, { useEffect, useMemo, useState } from 'react'
import axios from 'axios'

const mapLanguage = (language, otherLang) => {
  switch (language) {
    case 1:
      return 'BM'
    case 2:
      return 'Cina'
    case 3:
      return 'Inggeris'
    case 4:
      return `Lain (${otherLang || 'N/A'})`
    default:
      return '-'
  }
}

const mapCategoryOne = (v) => {
  switch (v) {
    case 1:
      return 'Adult'
    case 2:
      return 'Campus'
    case 3:
      return 'Youth'
    case 4:
      return 'Children'
    default:
      return '-'
  }
}

const mapCategoryTwo = (v) => {
  switch (v) {
    case 1:
      return 'Saint'
    case 2:
      return 'Seeking Christian'
    case 3:
      return 'Gospel Friend'
    case 4:
      return 'New Believer'
    case 5:
      return 'Elderly'
    default:
      return '-'
  }
}

const mapResponseOption = (opt) => {
  switch (opt) {
    case 1:
      return 'Kunjungan Rumah'
    case 2:
      return 'Pameran Buku'
    case 3:
      return 'Persidangan Khas'
    case 4:
      return 'Tempahan Buku'
    default:
      return ''
  }
}

// Reusable field component - now supports style prop
const InfoField = ({ label, value, style }) => (
  <div className='info-field'>
    <div className='info-label'>{label}</div>
    <div className='info-value' style={style}>
      {value || '-'}
    </div>
  </div>
)

const ResponseDetailsModal = ({
  show,
  onClose,
  recordId,
  currentUserId,
  onEdit,
}) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')

  const shortId = useMemo(() => {
    if (!recordId) return ''
    return recordId.slice(-6)
  }, [recordId])

  // Check if current user is the owner
  const isOwner = useMemo(() => {
    if (!data || !currentUserId) return false
    const recordUserId =
      typeof data.userId === 'object' ? data.userId?._id : data.userId
    return recordUserId?.toString() === currentUserId?.toString()
  }, [data, currentUserId])

  const handleEdit = () => {
    if (onEdit && recordId) {
      onEdit(recordId)
    }
  }

  useEffect(() => {
    if (show) document.body.classList.add('modal-open')
    else document.body.classList.remove('modal-open')

    return () => document.body.classList.remove('modal-open')
  }, [show])

  useEffect(() => {
    let cancelled = false

    const fetchOne = async () => {
      if (!show || !recordId) return
      try {
        setLoading(true)
        setError('')
        setData(null)

        const res = await axios.get(
          `/api/v1/user/sale-record/response/${recordId}`
        )
        if (!cancelled) setData(res.data.record)
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || e.message || 'Failed to load')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchOne()
    return () => {
      cancelled = true
    }
  }, [show, recordId])

  if (!show) return null

  const genderColor =
    data?.gender === 1 ? '#2f4483' : data?.gender === 2 ? '#78003c' : 'inherit'

  return (
    <>
      {/* backdrop */}
      <div
        className='modal-backdrop fade show'
        onClick={onClose}
        style={{ cursor: 'pointer' }}
      />

      <div
        className='modal fade show d-block'
        tabIndex='-1'
        role='dialog'
        aria-modal='true'
      >
        <div className='modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered'>
          <div className='modal-content'>
            {/* Header */}
            <div
              className='modal-header py-2'
              style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #556B2F',
              }}
            >
              <h6 className='modal-title mb-0' style={{ color: '#333' }}>
                Kad Respon (ID: {shortId || '-'})
              </h6>
              <button
                type='button'
                className='btn-close'
                aria-label='Close'
                onClick={onClose}
              />
            </div>

            {/* Body */}
            <div className='modal-body'>
              {loading && (
                <div
                  className='d-flex flex-column justify-content-center align-items-center'
                  style={{ minHeight: '150px' }}
                >
                  <div className='spinner-border text-success' role='status' />
                  <small className='mt-2 text-muted'>Memuatkan...</small>
                </div>
              )}

              {!loading && error && (
                <div className='alert alert-danger mb-0'>{error}</div>
              )}

              {!loading && !error && data && (
                <div className='response-details'>
                  {/* Personal Info */}
                  <div className='row g-2'>
                    <div className='col-6 col-md-6'>
                      <InfoField
                        label='Nama'
                        value={data.name}
                        style={{ color: genderColor, fontWeight: 600 }}
                      />
                    </div>
                    <div className='col-6 col-md-6'>
                      <InfoField
                        label='Tarikh'
                        value={
                          data.submitDate
                            ? new Date(data.submitDate).toLocaleDateString(
                                'en-GB',
                                {
                                  year: 'numeric',
                                  month: '2-digit',
                                  day: '2-digit',
                                }
                              )
                            : '-'
                        }
                      />
                    </div>

                    <div className='col-6 col-md-4'>
                      <InfoField label='Bangsa' value={data.race} />
                    </div>
                    <div className='col-6 col-md-4'>
                      <InfoField
                        label='Bahasa'
                        value={mapLanguage(data.language, data.otherLang)}
                      />
                    </div>
                    <div className='col-12 col-md-4'>
                      <InfoField
                        label='Kategori'
                        value={`${mapCategoryOne(
                          data.categoryOne
                        )} | ${mapCategoryTwo(data.categoryTwo)}`}
                      />
                    </div>
                  </div>

                  <hr className='my-2' />

                  {/* Contact Info */}
                  <div className='row g-2'>
                    <div className='col-12 col-md-6'>
                      <InfoField label='Telefon' value={data.phone} />
                    </div>
                    <div className='col-12 col-md-6'>
                      <InfoField label='Tempat' value={data.place?.placeName} />
                    </div>
                    <div className='col-12'>
                      <InfoField label='Alamat' value={data.address} />
                    </div>
                  </div>

                  <hr className='my-2' />

                  {/* Book Record */}
                  <div className='info-field'>
                    <div className='info-label'>Buku</div>
                    {Array.isArray(data.bookRecord) &&
                    data.bookRecord.length > 0 ? (
                      <div className='book-list'>
                        {data.bookRecord.map((b, idx) => (
                          <div key={idx} className='book-item'>
                            <span className='book-name'>{b.description}</span>
                            <span className='book-qty'>
                              x{b.qty}
                              {b.price && (
                                <small className='text-muted ms-1'>
                                  (RM {(b.price * b.qty).toFixed(2)})
                                </small>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className='info-value'>-</div>
                    )}
                  </div>

                  <hr className='my-2' />

                  {/* Response & Remarks */}
                  <div className='row g-2'>
                    <div className='col-6 col-md-8'>
                      <InfoField
                        label='Pilihan Respon'
                        value={
                          Array.isArray(data.responseOption) &&
                          data.responseOption.length > 0
                            ? data.responseOption
                                .map(mapResponseOption)
                                .filter(Boolean)
                                .join(', ')
                            : '-'
                        }
                      />
                    </div>
                    <div className='col-12'>
                      <InfoField label='Catatan' value={data.remark} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div
              className='modal-footer py-2'
              style={{ backgroundColor: '#f8f9fa' }}
            >
              {isOwner && (
                <button
                  type='button'
                  className='btn btn-primary btn-sm me-auto'
                  onClick={handleEdit}
                >
                  Edit
                </button>
              )}
              <button
                type='button'
                className='btn btn-secondary btn-sm'
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        body.modal-open { 
          overflow: hidden; 
        }

        .response-details {
          font-size: 0.9rem;
        }

        .info-field {
          margin-bottom: 0.25rem;
        }

        .info-label {
          font-size: 0.75rem;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .info-value {
          font-size: 0.9rem;
          color: #333;
          word-break: break-word;
        }

        .book-list {
          display: flex;
          flex-direction: column;
          margin-top: 0.25rem;
        }

        .book-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.1rem 0.6rem;
        }

        .book-name {
          flex: 1;
          margin-right: 0.5rem;
        }

        .book-qty {
          font-weight: 600;
          white-space: nowrap;
        }

        /* Mobile optimizations */
        @media (max-width: 576px) {
          .modal-dialog {
            margin: 0.5rem;
          }

          .info-label {
            font-size: 0.7rem;
          }

          .info-value {
            font-size: 0.85rem;
          }

          .book-item {
            padding: 0.35rem 0.5rem;
            font-size: 0.85rem;
          }

          .modal-content {
            max-height: 90vh;
          }
        }
      `}</style>
    </>
  )
}

export default ResponseDetailsModal
